import { GameAssetType, GameSpriteType } from '../../game-types';
import { GameServices } from '../../game-services';
import { Card } from 'src/games/phasersolitaire/logic/card/card';
import { PileId, PileType } from 'src/games/phasersolitaire/logic/logic-types';
import { BehaviorSubject, Subject } from 'rxjs';
import { assertNever } from 'src/games/common/helper';
import { CardType } from '../../../logic/logic-types';
import { CardId } from 'src/games/phasersolitaire/logic/card/card-id';
import { DragData } from '../board/board-view-model';
import { MoveCommand } from 'src/games/phasersolitaire/logic/commands/move-command';
import { PossibleMove } from 'src/games/phasersolitaire/logic/possible-moves-finder';
import { GameLogicDepth } from 'src/games/phasersolitaire/logic/game-logic-depth';
import { ResizeMetrics } from '../../resize-metrics';

export type OnDragStartAction = (dragData: DragData) => void;
export type OnDragAction = (dragData: DragData) => void;
export type OnDragEndAction = (dragData: DragData) => void;
export type OnShakeFromNoMatches = (cardViewModel: CardViewModel) => void;

export class CardViewModel {
  public static dragDistanceThreshold = 10;

  public get cardBackAsset() {
    return this.gameServices.assetCatalogueService.getAsset(GameAssetType.CardBack);
  }
  public get cardFrontAsset() {
    if (this.localCardId === undefined) {
      return this.gameServices.assetCatalogueService.getAsset(GameAssetType.CardHeart);
    }

    switch (this.localCardId.cardType) {
      case CardType.Club:
        return this.gameServices.assetCatalogueService.getAsset(GameAssetType.CardClub);
      case CardType.Spade:
        return this.gameServices.assetCatalogueService.getAsset(GameAssetType.CardSpade);
      case CardType.Heart:
        return this.gameServices.assetCatalogueService.getAsset(GameAssetType.CardHeart);
      case CardType.Diamond:
        return this.gameServices.assetCatalogueService.getAsset(GameAssetType.CardDiamond);
      default:
        assertNever(this.localCardId.cardType, 'Invalid card type');
    }
  }
  public get cardShadowAsset() {
    return this.gameServices.assetCatalogueService.getAsset(GameAssetType.CardShadow);
  }
  public get cardNumberLargeAsset() {
    if (this.localCardId === undefined) {
      return this.gameServices.assetCatalogueService.getSprite(GameSpriteType.NumbersLargePink);
    }
    return this.gameServices.assetCatalogueService.getSprite(
      this.localCardId.isRed ? GameSpriteType.NumbersLargePink : GameSpriteType.NumbersLargeBlack,
    );
  }
  public get cardNumberSmallAsset() {
    if (this.localCardId === undefined) {
      return this.gameServices.assetCatalogueService.getSprite(GameSpriteType.NumbersSmallPink);
    }
    return this.gameServices.assetCatalogueService.getSprite(
      this.localCardId.isRed ? GameSpriteType.NumbersSmallPink : GameSpriteType.NumbersSmallBlack,
    );
  }
  public get cardNumber() {
    if (this.localCardId === undefined) {
      return 0;
    }
    return this.localCardId.cardValue - 1;
  }
  public get isFaceUp() {
    return this.card.isFaceUp;
  }
  public get isInWaste() {
    return this.pileType === PileType.Waste;
  }
  public get canDrag() {
    return this.card.cardId !== undefined && this.isFaceUp && this.canAcceptInput;
  }
  public get isDragging() {
    return this._isDragging;
  }
  public get isStackDragging() {
    return this._isStackDragging;
  }
  public get isMoveCompleted() {
    return this.isMoveComplete;
  }
  public get canShowShadow() {
    return this.showShadow;
  }
  public get currentDepth() {
    return this.depth.asObservable();
  }
  public get onShakeTrigger() {
    return this.onShake.asObservable();
  }
  public get indexForPileId() {
    switch (this.pileId) {
      case PileId.Foundation_01:
        return 0;
      case PileId.Foundation_02:
        return 1;
      case PileId.Foundation_03:
        return 2;
      case PileId.Foundation_04:
        return 3;
      case PileId.Tableau_01:
        return 0;
      case PileId.Tableau_02:
        return 1;
      case PileId.Tableau_03:
        return 2;
      case PileId.Tableau_04:
        return 3;
      case PileId.Tableau_05:
        return 4;
      case PileId.Tableau_06:
        return 5;
      case PileId.Tableau_07:
        return 6;
      case PileId.Waste:
        return 0;
      case PileId.Deck:
        return 0;
      default:
        assertNever(this.pileId, 'Invalid PileId');
    }
  }

  public set onDragStartAction(value: OnDragStartAction) {
    this.dragStartAction = value;
  }
  public set onDragAction(value: OnDragAction) {
    this.dragAction = value;
  }
  public set onDragEndAction(value: OnDragEndAction) {
    this.dragEndAction = value;
  }
  public set onShakeFromNoMatchesAction(value: OnShakeFromNoMatches) {
    this.shakeFromNoMatchesAction = value;
  }
  public set enableInput(enable: boolean) {
    this.canAcceptInput = enable;
  }

  private readonly gameServices: GameServices;
  private readonly gameLogicDepth: GameLogicDepth;
  private card: Card;
  private localCardId?: CardId;
  private stackIndex: number;
  private pileType: PileType;
  private pileId: PileId;
  private localFaceUp: boolean;
  private showShadow: boolean;
  private _isDragging: boolean;
  private _isStackDragging: boolean;
  private isMoving: boolean;
  private isMoveComplete: boolean;
  private canAcceptInput: boolean;
  private readonly depth: BehaviorSubject<number>;
  private readonly onShake: Subject<void>;
  private dragStartAction?: OnDragStartAction;
  private dragAction?: OnDragAction;
  private dragEndAction?: OnDragEndAction;
  private shakeFromNoMatchesAction?: OnShakeFromNoMatches;

  constructor(
    gameServices: GameServices,
    gameLogicDepth: GameLogicDepth,
    card: Card,
    stackIndex: number,
    pileType: PileType,
    pileId: PileId,
  ) {
    this.gameServices = gameServices;
    this.gameLogicDepth = gameLogicDepth;
    this.card = card;
    this.stackIndex = stackIndex;
    this.pileType = pileType;
    this.pileId = pileId;
    this.localFaceUp = false;
    this.showShadow = false;
    this._isDragging = false;
    this._isStackDragging = false;
    this.isMoving = false;
    this.isMoveComplete = true;
    this.canAcceptInput = false;

    this.depth = new BehaviorSubject<number>(gameLogicDepth.cardDepth(this.card));
    this.onShake = new Subject<void>();
  }

  public performClick(): boolean {
    if (this.pileType == PileType.Deck) {
      this.gameServices.gameRunnerService.drawNextCard();
    } else {
      if (!this.canDrag) {
        return false;
      }

      const hasNoMatches = this.gameServices.gameRunnerService.autoMoveCard(this.card);
      if (!hasNoMatches) {
        this.shakeFromNoMatchesAction?.(this);
      } else {
        return true;
      }
    }
    return false;
  }

  public canRefreshCard() {
    if (this.card.cardId === undefined || this.localCardId === this.card.cardId) {
      return false;
    }
    this.localCardId = this.card.cardId;
    return true;
  }

  public canFlipCard() {
    const shouldBeOpened = this.card.cardId !== undefined && this.card.isFaceUp;
    if (this.localFaceUp === shouldBeOpened) {
      return false;
    }
    this.localFaceUp = shouldBeOpened;
    return true;
  }

  public canShakeForHints(possibleMoves: PossibleMove[]): boolean {
    return possibleMoves.some(
      (possibleMove) =>
        possibleMove.cardIndex === this.card.index && possibleMove.originPileId === this.pileId,
    );
  }

  public shakeCard() {
    this.onShake.next();
  }

  public refreshShadow() {
    if (
      this.pileType === PileType.Deck ||
      this.pileType === PileType.Waste ||
      this.pileType === PileType.Foundation
    ) {
      this.showShadow = this.stackIndex === 0 || this.isDragging || this.isMoving;
    } else {
      this.showShadow = true;
    }
  }

  public dragStart(dragX: number, dragY: number) {
    this.dragStartAction?.({
      dragX: dragX,
      dragY: dragY,
      cardViewModel: this,
    });
    this._isDragging = true;
  }

  public drag(dragX: number, dragY: number) {
    this.dragAction?.({
      dragX: dragX,
      dragY: dragY,
      cardViewModel: this,
    });
    this.depth.next(this.gameLogicDepth.dragCardDepth(this.card));
  }

  public dragEnd(dragX: number, dragY: number) {
    this.dragEndAction?.({
      dragX: dragX,
      dragY: dragY,
      cardViewModel: this,
    });
    this._isDragging = false;
  }

  public canDragInStack(dragData: DragData) {
    if (dragData.cardViewModel.card.index === this.card.index) {
      return false;
    }
    return (
      this.pileType === PileType.Tableau &&
      this.pileId === dragData.cardViewModel.pileId &&
      this.stackIndex > dragData.cardViewModel.stackIndex
    );
  }

  public isHigherInTableauStack(cardViewModel: CardViewModel): boolean {
    if (
      this.pileType === PileType.Tableau &&
      this.pileId === cardViewModel.pileId &&
      this.stackIndex > cardViewModel.stackIndex
    ) {
      return true;
    }
    return false;
  }

  public setStackDragging(enable: boolean) {
    this._isStackDragging = enable;

    if (enable) {
      this.depth.next(this.stackIndex + this.gameLogicDepth.dragCardDepth(this.card));
    }
  }

  public draggingOver(draggingViewModel: CardViewModel) {
    if (!this.isFaceUp || draggingViewModel.card.index === this.card.index) {
      return;
    }
  }

  public droppedOver(draggingViewModel: CardViewModel) {
    if (
      !this.isFaceUp ||
      draggingViewModel.card.index === this.card.index ||
      draggingViewModel.card.cardId === undefined
    ) {
      return;
    }
    const game = this.gameServices.gameRunnerService.currentGame;
    if (game === undefined) {
      return;
    }

    const originPile = game.board.getPileByCardIndex(draggingViewModel.card.index);
    const targetPile = game.board.getPileByCardIndex(this.card.index);

    if (draggingViewModel.card.canMove(originPile, targetPile)) {
      game.execute(new MoveCommand(draggingViewModel.card.cardId.id, targetPile.id));
    }
  }

  public droppedOntoPile(pileId: PileId) {
    const game = this.gameServices.gameRunnerService.currentGame;
    if (game === undefined || this.card.cardId === undefined) {
      return;
    }

    const originPile = game.board.getPileByCardIndex(this.card.index);
    const targetPile = game.board.getPileByPileId(pileId);

    if (this.card.canMove(originPile, targetPile)) {
      game.execute(new MoveCommand(this.card.cardId.id, targetPile.id));
    }
  }

  public setMovement(movement: number) {
    if (!this.isMoveComplete && movement < 0.1) {
      this.isMoveComplete = true;
      this.gameServices.gameRunnerService.onCardPlaced(this.card);
    }

    if (movement < 0.1 && this.depth.value != this.gameLogicDepth.cardDepth(this.card)) {
      this.depth.next(this.gameLogicDepth.cardDepth(this.card));
    }

    // TODO: Do we track if we're moving?
    // this.isMoving = movement > 0.1;
  }

  public calculateTargetPosition(resizeMetrics: ResizeMetrics): { x: number; y: number } {
    const layoutConfig = resizeMetrics.layoutConfig;

    const scaleFactor = resizeMetrics.scalingMetrics.scaleFactor;
    const scaledOffsetX = resizeMetrics.scalingMetrics.scaledOffsetX;
    const scaledPaddingX = (layoutConfig.boardWidth - layoutConfig.gameWidth) * 0.5 * scaleFactor;

    const offsetX = scaledOffsetX + scaledPaddingX;

    switch (this.pileType) {
      case PileType.Deck: {
        const targetPosX =
          offsetX +
          ((layoutConfig.pileWidth + layoutConfig.hSpacing) * 6 + layoutConfig.pileWidth * 0.5) *
            scaleFactor;
        const targetPosY = (layoutConfig.vPadding + layoutConfig.pileHeight * 0.5) * scaleFactor;

        return {
          x: targetPosX,
          y: targetPosY,
        };
      }
      case PileType.Waste: {
        const targetPosX =
          offsetX +
          ((layoutConfig.pileWidth + layoutConfig.hSpacing) * 4.5 + layoutConfig.pileWidth * 0.5) *
            scaleFactor;
        const targetPosY = (layoutConfig.vPadding + layoutConfig.pileHeight * 0.5) * scaleFactor;
        return {
          x: targetPosX,
          y: targetPosY,
        };
      }
      case PileType.Foundation: {
        const targetPosX =
          offsetX +
          (this.indexForPileId * (layoutConfig.pileWidth + layoutConfig.hSpacing) +
            layoutConfig.pileWidth * 0.5) *
            scaleFactor;
        const targetPosY = (layoutConfig.vPadding + layoutConfig.pileHeight * 0.5) * scaleFactor;
        return {
          x: targetPosX,
          y: targetPosY,
        };
      }
      case PileType.Tableau: {
        const targetPosX =
          offsetX +
          (this.indexForPileId * (layoutConfig.pileWidth + layoutConfig.hSpacing) +
            layoutConfig.pileWidth * 0.5) *
            scaleFactor;

        const targetPosY =
          (this.stackIndex * layoutConfig.stackSpacing +
            layoutConfig.vPadding +
            layoutConfig.vSpacing +
            layoutConfig.pileHeight * 1.5) *
          scaleFactor;
        return {
          x: targetPosX,
          y: targetPosY,
        };
      }
      default:
        assertNever(this.pileType, 'Invalid pile type');
    }
  }

  public matchesCardIndex(index: number): boolean {
    return this.card.index === index;
  }

  public refreshPosition(stackIndex: number, card: Card, pileType: PileType, pileId: PileId) {
    if (this.card !== card) {
      this.card = card;
    }
    if (this.depth.value !== this.gameLogicDepth.cardDepth(card)) {
      this.depth.next(this.gameLogicDepth.cardDepth(card));
    }
    if (this.stackIndex !== stackIndex) {
      this.stackIndex = stackIndex;

      this.isMoveComplete = false;
    }
    if (this.pileType !== pileType) {
      this.pileType = pileType;

      this.isMoveComplete = false;
    }
    if (this.pileId !== pileId) {
      this.pileId = pileId;

      this.isMoveComplete = false;
    }
  }
}
